import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { useProject } from 'hooks/useProject'
import { useResolvedTheme } from 'hooks/useTheme'
import Button from 'components/button'
import DeleteModal from 'components/delete'
import { GridContext, readFile, runInstant, runWrite, saveFile, uploadFile, writeErrorMessage } from './bridge'
import {
  buildPageDocument,
  FILE_TIMEOUT_MS,
  isBridgeRequest,
  isOwnRequest,
  PageFile,
  PageLibraries,
  PageLibrary
} from './runtime'
import { readSnapshot } from './source'
import { pageAccess } from './access'
import { pageTheme } from './theme'
import { loadIfcWasm, loadLibraries, pageLibraries } from './libraries'
import { CAPTURE_TIMEOUT_MS, captureReply, CaptureReply, loadCaptureLibrary, registerCapture } from './capture'
import PageSkeleton from './skeleton'
import { IFile } from 'types'

// A page deleting more rows than this at once asks the person first, in Morta's own dialog rather than one the
// page draws, so a page can't delete a table's rows in bulk without them knowing.
export const BULK_DELETE_CONFIRM = 10

// Files a page may be reading or uploading at once: each is held in memory until it has moved, and models can be large.
export const MAX_FILE_READS = 4

// What couldn't load, as the reader knows it: the page's model viewer or its charts.
function libraryFailure(needed: PageLibrary[]): string {
  if (needed.includes('three')) return "The 3D model viewer on this page couldn't load."
  return "The charts on this page couldn't load."
}

/** Why a read-only page (unsaved HTML, or an older version being looked at) refuses a write. */
export const PREVIEW_WRITE_ERROR =
  "This page is shown read-only, as a preview or an older version, so it can't change rows."

/**
 * Runs a Custom View page against the table view's data context: morta.onData reads the rows the grid
 * holds (sorted and filtered by the reader's current toolbar settings, streamed with their access) and
 * writes go through the grid's own row functions, so both behave exactly as in the table view.
 */
export default function CustomViewFrame({ html, readOnly = false }: { html: string; readOnly?: boolean }) {
  const { spreadsheetData, setSpreadsheetData, handleCreateRow } = useDataContext()
  // The hub's colours and the app's light or dark theme reach the page as CSS variables; a change to either
  // reloads it.
  const { project } = useProject()
  const mode = useResolvedTheme()
  const theme = JSON.stringify(pageTheme(project, mode))
  // A library (d3, three.js, the IFC viewer) goes into a page only when its code uses it, fetched the first time
  // one does.
  const needed = pageLibraries(html)
  const neededKey = needed.join(',')
  const [libraryScripts, setLibraryScripts] = useState<{ key: string; scripts: PageLibraries }>({
    key: '',
    scripts: {}
  })
  // A failed fetch (offline, or a deploy replaced the chunk) shows a way to try again rather than a blank view.
  // It's kept for the libraries that failed: a page edited to need others fetches them.
  const [failedKey, setFailedKey] = useState<string | null>(null)
  const librariesFailed = failedKey === neededKey
  useEffect(() => {
    if (libraryScripts.key === neededKey || librariesFailed) return
    let live = true
    loadLibraries(pageLibraries(html)).then(
      (scripts) => {
        if (live) setLibraryScripts({ key: neededKey, scripts })
      },
      () => {
        if (live) setFailedKey(neededKey)
      }
    )
    return () => {
      live = false
    }
  }, [html, neededKey, libraryScripts, librariesFailed])
  const libraries = libraryScripts.key === neededKey ? libraryScripts.scripts : null
  const frame = useRef<HTMLIFrameElement>(null)
  // A document export takes its picture of the page through this element (see capture.ts).
  const shell = useRef<HTMLDivElement>(null)
  // The channel of the document the frame last loaded; see `loaded`.
  const loadedChannel = useRef<string>(undefined)
  // The channel of the document on show. Until a document has loaded, the frame is the browser's blank white
  // page, so it stays hidden behind the page's skeleton.
  const [shownChannel, setShownChannel] = useState<string>()
  const [deleteRequest, setDeleteRequest] = useState<{ count: number; answer: (confirmed: boolean) => void }>()
  // A new generation reloads the page, e.g. when hidden columns change the columns it reads.
  const [generation, setGeneration] = useState(0)
  // The callback must read `generation`: the React Compiler keys this memo on what the callback uses, so
  // an unread dependency would be dropped and the page would never reload.
  const document = useMemo(() => {
    const channel = crypto.randomUUID()
    const hubTheme = JSON.parse(theme)
    return {
      generation,
      channel,
      theme: hubTheme,
      html: buildPageDocument(html, channel, crypto.randomUUID(), hubTheme, libraries ?? {})
    }
  }, [html, generation, theme, libraries])

  const latest = useRef(spreadsheetData)
  // The provider re-creates handleCreateRow each render; read it through a ref so the bridge isn't restarted.
  const createRow = useRef(handleCreateRow)
  // A preview of unsaved HTML (Edit HTML) reads the rows but never writes them; read through a ref so turning it off
  // when the text is saved doesn't restart the bridge.
  const previewOnly = useRef(readOnly)
  const notify = useRef<() => void>(() => {})
  const version = useRef(spreadsheetData.dataVersion)
  // What the page may do (the row settings and the reader's role): it was handed this when it
  // loaded, so a change reloads it, as hiding columns does.
  const accessOf = (data: typeof spreadsheetData) => JSON.stringify(pageAccess(data))
  const seen = useRef({
    rows: spreadsheetData.rows,
    bitmap: spreadsheetData.visibleBitmap,
    hidden: spreadsheetData.userConfiguration.hiddenColumns.join(','),
    access: accessOf(spreadsheetData)
  })

  // Follow the data context. Any change to the rows (grid edits, adds, deletes and moves, other people's edits
  // arriving over the websocket, the page's own writes) is passed to the page, whose onData runs again with them.
  // So are the reader's toolbar filters, sorts, slicers and search, once the grid has re-applied them (a new row
  // order or visibility bitmap): the page keeps what it has built (a selection, scroll, a 3D model and its camera)
  // rather than reloading. Hiding or showing columns, or a change to what the page may do, reloads it.
  useLayoutEffect(() => {
    latest.current = spreadsheetData
    createRow.current = handleCreateRow
    previewOnly.current = readOnly
    const before = seen.current
    const now = {
      rows: spreadsheetData.rows,
      bitmap: spreadsheetData.visibleBitmap,
      hidden: spreadsheetData.userConfiguration.hiddenColumns.join(','),
      access: accessOf(spreadsheetData)
    }
    const reshaped = now.rows !== before.rows || now.bitmap !== before.bitmap
    if (now.hidden !== before.hidden || now.access !== before.access) setGeneration((value) => value + 1)
    else if (reshaped || spreadsheetData.dataVersion !== version.current) notify.current()
    seen.current = now
    version.current = spreadsheetData.dataVersion
  })

  // A layout effect, not useEffect: the listener must exist before the srcdoc iframe can run its
  // script. A passive effect may flush after the page has already posted its first data request,
  // which is then dropped and the page's request times out.
  useLayoutEffect(() => {
    let disposed = false
    // One write at a time keeps a page's writes in order. There are no per-page budgets: like the table view,
    // reads come from the rows the grid already holds.
    let busy = false
    // A bulk delete waiting on the person, answered no if the page goes away first.
    let pendingAnswer: ((confirmed: boolean) => void) | undefined
    const requests = new Set<number>()
    // Pictures the page is drawing for an export, by their request's id.
    const capturing = new Map<number, (reply: CaptureReply) => void>()
    const captureIds = { last: 0 }
    // Stops file downloads still running when the page goes away.
    const downloads = new AbortController()
    let reading = 0
    const grid: GridContext = {
      projectId: latest.current.tableDetails.projectPublicId,
      current: () => latest.current,
      dispatch: setSpreadsheetData,
      createRow: (...args) => createRow.current(...args),
      theme: document.theme
    }

    // A file's bytes are transferred to the page rather than copied.
    const post = (message: object, transfer: Transferable[] = []) => {
      const target = frame.current?.contentWindow
      if (!disposed && target) target.postMessage({ channel: document.channel, ...message }, '*', transfer)
    }
    notify.current = () => post({ event: 'change' })

    // The page listens for messages once its document has loaded; an export can ask for its picture before then
    // (it renders embeds just before asking), and a message sent earlier would be lost.
    const pageLoaded = new Promise<void>((resolve) => {
      if (loadedChannel.current === document.channel) resolve()
      else frame.current?.addEventListener('load', () => resolve(), { once: true })
    })
    const unregister = shell.current
      ? registerCapture(
          shell.current,
          (scale) =>
            new Promise((resolve, reject) => {
              const id = ++captureIds.last
              // Counts the wait for the page to load too, so a page that never loads doesn't hold up the export.
              const timer = setTimeout(() => {
                capturing.delete(id)
                reject(new Error('The Custom View took too long to draw.'))
              }, CAPTURE_TIMEOUT_MS)
              capturing.set(id, (reply) => {
                clearTimeout(timer)
                capturing.delete(id)
                if ('error' in reply) reject(new Error(reply.error))
                else resolve(reply)
              })
              Promise.all([loadCaptureLibrary(), pageLoaded]).then(
                ([library]) => post({ event: 'capture', id, library, scale }),
                () => capturing.get(id)?.({ error: "Morta couldn't load what it draws the page's picture with." })
              )
            })
        )
      : () => {}

    const receive = async (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow || event.origin !== 'null') return
      // A press inside the page: replay it on the frame, so the app's click-outside handlers (menus, popovers)
      // see a press outside whatever is open, as they would for any other part of the view.
      if (event.data?.channel === document.channel && event.data.event === 'pointerdown') {
        frame.current?.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }))
        return
      }
      if (event.data?.channel === document.channel && event.data.event === 'captured') {
        capturing.get(event.data.id)?.(
          captureReply(event.data) ?? { error: "The Custom View's picture wasn't a picture Morta could use." }
        )
        return
      }
      if (!isOwnRequest(event.data, document.channel) || requests.has(event.data.id)) return
      const request = event.data
      requests.add(request.id)
      const reply = (payload: object) => post({ id: request.id, ...payload })
      if (!isBridgeRequest(request, document.channel)) {
        reply({
          error:
            'Invalid request. Check the method name, and that rows and columns are identified by the IDs morta.onData gives.'
        })
        return
      }
      // Options and colours come straight from the columns the grid holds and change nothing, so a form can
      // refresh a dependent dropdown while a preview or save is still running.
      if (request.method === 'options' || request.method === 'colour' || request.method === 'bind') {
        try {
          reply({ result: runInstant(request, latest.current, document.theme.mode) })
        } catch (caught) {
          reply({ error: writeErrorMessage(caught) })
        }
        return
      }
      // morta.onData's own read: every row at once, alongside whatever the page is doing.
      if (request.method === 'snapshot') {
        reply({ result: readSnapshot(latest.current, document.theme) })
        return
      }
      // web-ifc's wasm for the page's IFC viewer, a copy of Morta's own handed over to the page.
      if (request.method === 'ifcWasm') {
        try {
          const wasm = (await loadIfcWasm()).slice(0)
          post({ id: request.id, result: wasm }, [wasm])
        } catch (caught) {
          reply({ error: writeErrorMessage(caught) })
        }
        return
      }
      // A file is read alongside whatever else the page is doing: a large model mustn't hold up its writes. A download
      // reads it the same way, then Morta saves it rather than handing it to the page. An upload stores a file the
      // page has, so it shares those places, but it changes the table's files and a preview mustn't.
      if (request.method === 'file' || request.method === 'download' || request.method === 'upload') {
        if (request.method === 'upload' && previewOnly.current) {
          reply({ error: PREVIEW_WRITE_ERROR })
          return
        }
        if (reading >= MAX_FILE_READS) {
          reply({ error: `A page can read or upload ${MAX_FILE_READS} files at once. Wait for one to finish first.` })
          return
        }
        reading = reading + 1
        // Stopped when the page goes away, or once the page has given up waiting, so a stalled download or upload
        // frees its place for another.
        const download = new AbortController()
        const stop = () => download.abort()
        downloads.signal.addEventListener('abort', stop)
        const deadline = setTimeout(stop, FILE_TIMEOUT_MS)
        let file: PageFile | undefined
        let uploaded: IFile | undefined
        try {
          if (request.method === 'upload') uploaded = await uploadFile(request.params, latest.current, download.signal)
          else file = await readFile(request.params, latest.current, download.signal)
        } catch (caught) {
          reply({ error: writeErrorMessage(caught) })
        }
        clearTimeout(deadline)
        downloads.signal.removeEventListener('abort', stop)
        reading = reading - 1
        // Nothing is saved for a page that has gone away.
        if (file && request.method === 'download' && !disposed) {
          saveFile(file)
          reply({ result: { name: file.name } })
        } else if (file) post({ id: request.id, result: file }, [file.data])
        else if (uploaded) reply({ result: uploaded })
        return
      }
      // A form's preview of a row saves nothing, so it runs in a preview too.
      if (previewOnly.current && request.method !== 'previewRow') {
        reply({ error: PREVIEW_WRITE_ERROR })
        return
      }
      if (busy) {
        reply({ error: 'Another request is still running. Wait for it to finish before sending the next one.' })
        return
      }
      busy = true
      if (request.method === 'deleteRows' && request.params.ids.length > BULK_DELETE_CONFIRM) {
        const confirmed = await new Promise<boolean>((answer) => {
          pendingAnswer = answer
          setDeleteRequest({ count: request.params.ids.length, answer })
        })
        pendingAnswer = undefined
        if (!confirmed) {
          reply({ error: 'The person chose not to delete the rows.' })
          busy = false
          return
        }
      }
      // Plain statements only in the try: the React Compiler can't take a finally or a conditional expression there.
      try {
        reply({ result: await runWrite(request, grid) })
      } catch (caught) {
        reply({ error: writeErrorMessage(caught) })
      }
      busy = false
    }

    window.addEventListener('message', receive)
    return () => {
      disposed = true
      downloads.abort()
      unregister()
      capturing.forEach((answer) => answer({ error: 'The Custom View reloaded before its picture was taken.' }))
      pendingAnswer?.(false)
      setDeleteRequest(undefined)
      notify.current = () => {}
      window.removeEventListener('message', receive)
    }
  }, [document, setSpreadsheetData])

  // A page that uses a library waits for it, rather than running without it.
  if (libraries === null) {
    if (librariesFailed) {
      return (
        <div role="alert" className="flex flex-col items-center gap-3 p-10 text-sm text-muted">
          <span>{libraryFailure(needed)}</span>
          <Button internalType="outline" onClick={() => setFailedKey(null)}>
            Try again
          </Button>
        </div>
      )
    }
    return <PageSkeleton testId="custom-view-loading-libraries" />
  }

  // The frame loads each document once. Loading it again means the page navigated its frame (which the sandbox
  // can't stop, and which could carry data in the address), so the page is rebuilt.
  const loaded = () => {
    if (loadedChannel.current !== document.channel) {
      loadedChannel.current = document.channel
      setShownChannel(document.channel)
      return
    }
    setGeneration((value) => value + 1)
  }

  const answerDelete = (confirmed: boolean) => {
    deleteRequest?.answer(confirmed)
    setDeleteRequest(undefined)
  }

  const shown = shownChannel === document.channel

  return (
    <div
      ref={shell}
      data-custom-view-capture=""
      data-table-id={spreadsheetData.tableDetails.publicId}
      data-view-id={spreadsheetData.viewDetails.publicId}
      className="relative flex flex-col flex-1 min-h-0"
    >
      {!shown && <PageSkeleton className="absolute inset-0 z-[1]" />}
      <iframe
        ref={frame}
        title="Custom view"
        // allow-forms only lets `submit` events fire (the browser drops them otherwise, before any handler
        // runs); the page CSP's form-action 'none' still stops a submission from going anywhere.
        sandbox="allow-scripts allow-forms"
        referrerPolicy="no-referrer"
        srcDoc={document.html}
        onLoad={loaded}
        // Exactly the space it's given, with no minimum: an embed shorter than a minimum would scroll the frame
        // as well as the page inside it. The page scrolls itself.
        className={`w-full border-0 flex-1 transition-opacity duration-150 ${shown ? 'opacity-100' : 'opacity-0'}`}
      />
      {deleteRequest && (
        <DeleteModal
          id="custom-view-bulk-delete"
          open
          setOpen={(open) => {
            if (!open) answerDelete(false)
          }}
          resourceName={`${deleteRequest.count} rows`}
          customHeading={`Delete ${deleteRequest.count.toLocaleString()} rows?`}
          confirmLabel="Delete rows"
          confirmByTyping={false}
          otherContent={
            <p className="text-sm text-secondary">
              This page is asking to delete {deleteRequest.count.toLocaleString()} rows from the table at once. Only
              delete them if that&apos;s what you meant to do.
            </p>
          }
          deleteResource={async () => answerDelete(true)}
        />
      )}
    </div>
  )
}
